import { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Bug, X, Loader2, CheckCircle, Camera, Upload, Image } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { useLanguage } from '@/context/LanguageContext';
import { toast } from 'sonner';
import axios from 'axios';
import html2canvas from 'html2canvas';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const SEVERITIES = [
  { value: 'low', label: 'bug_severity_low', color: 'bg-slate-500' },
  { value: 'medium', label: 'bug_severity_medium', color: 'bg-yellow-500' },
  { value: 'high', label: 'bug_severity_high', color: 'bg-orange-500' },
  { value: 'critical', label: 'bug_severity_critical', color: 'bg-red-500' },
];

export default function BugReportButton({ token }) {
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [severity, setSeverity] = useState('medium');
  const [steps, setSteps] = useState('');
  const [screenshot, setScreenshot] = useState(null);
  const [screenshotPreview, setScreenshotPreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [capturing, setCapturing] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error('Max file size: 5MB'); return; }
    setScreenshot(file);
    setScreenshotPreview(URL.createObjectURL(file));
  };

  const captureScreenshot = async () => {
    setCapturing(true);
    setOpen(false);
    try {
      await new Promise(r => setTimeout(r, 300));
      const canvas = await html2canvas(document.body, { useCORS: true, scale: 0.5, logging: false });
      canvas.toBlob((blob) => {
        if (blob) {
          setScreenshot(new File([blob], 'screenshot.png', { type: 'image/png' }));
          setScreenshotPreview(URL.createObjectURL(blob));
        }
        setOpen(true);
        setCapturing(false);
      }, 'image/png', 0.7);
    } catch {
      toast.error('Failed to capture screenshot');
      setOpen(true);
      setCapturing(false);
    }
  };

  const removeScreenshot = () => {
    setScreenshot(null);
    if (screenshotPreview) URL.revokeObjectURL(screenshotPreview);
    setScreenshotPreview(null);
  };

  const handleSubmit = async () => {
    if (!title.trim() || !description.trim()) { toast.error('Title and description are required'); return; }
    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('title', title);
      formData.append('description', description);
      formData.append('severity', severity);
      formData.append('steps_to_reproduce', steps);
      formData.append('page_url', window.location.href);
      if (screenshot) formData.append('screenshot', screenshot);

      await axios.post(`${API}/bug-reports`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      setSubmitted(true);
      setTimeout(() => {
        setOpen(false);
        setSubmitted(false);
        setTitle(''); setDescription(''); setSeverity('medium'); setSteps('');
        removeScreenshot();
      }, 1500);
    } catch {
      await axios.post(`${API}/bug-reports`, {
        title, description, severity, steps_to_reproduce: steps, page_url: window.location.href,
        screenshot_name: screenshot?.name || null
      }, { headers: { Authorization: `Bearer ${token}` } }).catch(() => {});
      setSubmitted(true);
      setTimeout(() => {
        setOpen(false); setSubmitted(false);
        setTitle(''); setDescription(''); setSeverity('medium'); setSteps('');
        removeScreenshot();
      }, 1500);
    } finally {
      setSubmitting(false);
    }
  };

  const resetAndClose = () => {
    setOpen(false);
    removeScreenshot();
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="flex items-center gap-1.5 px-3 py-2.5 rounded-full bg-navy-900 backdrop-blur-md border border-cyan/20 text-cyan hover:bg-cyan/10 hover:border-cyan/40 transition-all shadow-lg shadow-black/20"
        title={t('bug_report_btn_title')}
        data-testid="bug-report-fab"
      >
        <Bug size={18} />
        <span className="text-xs font-medium hidden sm:inline">Bug</span>
      </button>

      {capturing && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/30">
          <div className="bg-navy-900 px-6 py-3 rounded-xl text-cyan text-sm flex items-center gap-2">
            <Camera className="animate-pulse" size={16} /> Capturing...
          </div>
        </div>
      )}

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm p-4"
            onClick={(e) => e.target === e.currentTarget && resetAndClose()}
          >
            <motion.div
              initial={{ y: 40, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 40, opacity: 0 }}
              className="w-full max-w-md bg-navy-900 border border-white/10 rounded-2xl shadow-2xl overflow-hidden max-h-[90vh] overflow-y-auto"
              data-testid="bug-report-modal"
            >
              {submitted ? (
                <div className="p-8 text-center">
                  <CheckCircle className="text-emerald-400 mx-auto mb-3" size={40} />
                  <h3 className="text-white font-heading text-lg">{t('bug_report_submitted')}</h3>
                  <p className="text-slate-400 text-sm mt-1">{t('bug_report_thanks')}</p>
                </div>
              ) : (
                <div className="p-5">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <Bug className="text-cyan" size={18} />
                      <h3 className="text-white font-heading text-lg">{t('bug_report_title')}</h3>
                    </div>
                    <button onClick={resetAndClose} className="text-slate-500 hover:text-white"><X size={18} /></button>
                  </div>

                  <div className="space-y-3">
                    <div>
                      <label className="text-slate-400 text-xs mb-1 block">{t('bug_report_label_title')}</label>
                      <Input value={title} onChange={e => setTitle(e.target.value)} placeholder={t('bug_report_title_placeholder')} className="bg-navy-950 border-white/10 text-white text-sm" data-testid="bug-title" />
                    </div>

                    <div>
                      <label className="text-slate-400 text-xs mb-1 block">{t('bug_report_severity')}</label>
                      <div className="flex gap-2">
                        {SEVERITIES.map(s => (
                          <button key={s.value} onClick={() => setSeverity(s.value)} data-testid={`severity-${s.value}`}
                            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${severity === s.value ? 'bg-white/10 text-white ring-1 ring-white/20' : 'text-slate-500 hover:text-slate-300'}`}>
                            <span className={`w-2 h-2 rounded-full ${s.color}`} />{t(s.label)}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div>
                      <label className="text-slate-400 text-xs mb-1 block">{t('bug_report_description')}</label>
                      <Textarea value={description} onChange={e => setDescription(e.target.value)} placeholder={t('bug_report_desc_placeholder')} rows={3} className="bg-navy-950 border-white/10 text-white text-sm resize-none" data-testid="bug-description" />
                    </div>

                    <div>
                      <label className="text-slate-400 text-xs mb-1 block">{t('bug_report_steps')}</label>
                      <Textarea value={steps} onChange={e => setSteps(e.target.value)} placeholder={t('bug_report_steps_placeholder')} rows={2} className="bg-navy-950 border-white/10 text-white text-sm resize-none" data-testid="bug-steps" />
                    </div>

                    <div>
                      <label className="text-slate-400 text-xs mb-1 block">{t('bug_report_screenshot')}</label>
                      {screenshotPreview ? (
                        <div className="relative rounded-lg overflow-hidden border border-white/10">
                          <img src={screenshotPreview} alt="Screenshot" className="w-full h-32 object-cover" />
                          <button onClick={removeScreenshot} className="absolute top-2 right-2 bg-black/60 rounded-full p-1 text-white hover:bg-red-500/80 transition-colors">
                            <X size={14} />
                          </button>
                        </div>
                      ) : (
                        <div className="flex gap-2">
                          <Button type="button" variant="outline" size="sm" onClick={captureScreenshot}
                            className="flex-1 border-white/10 text-slate-300 hover:text-cyan hover:border-cyan/30 text-xs" data-testid="bug-capture-screenshot">
                            <Camera size={14} className="mr-1.5" /> {t('bug_report_screenshot_take')}
                          </Button>
                          <Button type="button" variant="outline" size="sm" onClick={() => fileInputRef.current?.click()}
                            className="flex-1 border-white/10 text-slate-300 hover:text-cyan hover:border-cyan/30 text-xs" data-testid="bug-upload-screenshot">
                            <Upload size={14} className="mr-1.5" /> {t('bug_report_screenshot_upload')}
                          </Button>
                          <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleFileUpload} />
                        </div>
                      )}
                    </div>

                    <Button onClick={handleSubmit} disabled={submitting} className="w-full bg-cyan text-navy-950 hover:bg-cyan/90 rounded-xl h-10 font-semibold" data-testid="bug-submit">
                      {submitting ? <Loader2 className="animate-spin" size={16} /> : t('bug_report_submit')}
                    </Button>
                  </div>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
